import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Space, Tag, Typography } from 'antd';
import {
  getFitfinderSession,
  transitionFitfinderSession,
  type FitfinderDealMatchSnapshot,
  type FitfinderSession,
} from '../../services/api/fitfinderService';
import { getApiErrorMessage } from '../../utils/apiErrors';

const { Title, Paragraph, Text } = Typography;

/** Path segment must look like a UUID before GET `/fitfinder/sessions/{id}` (backend returns 400 otherwise). */
const SESSION_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SessionDealMatchAlert: React.FC<{ deal: FitfinderDealMatchSnapshot }> = ({ deal }) => (
  <Alert
    type="info"
    showIcon
    style={{ marginTop: 16 }}
    message="Match snapshot (from deal artifact)"
    description={
      <span>
        {deal.fitfinder_match_score_tier != null && (
          <>
            Tier: <Text strong>{String(deal.fitfinder_match_score_tier)}</Text>
            <br />
          </>
        )}
        {deal.fitfinder_matching_inputs_ready != null && (
          <>
            Inputs ready: <Text strong>{deal.fitfinder_matching_inputs_ready ? 'Yes' : 'No'}</Text>
            <br />
          </>
        )}
        {deal.fitfinder_matching_inputs_snapshot != null && (
          <>
            Summary lengths (privacy-safe): {JSON.stringify(deal.fitfinder_matching_inputs_snapshot)}
          </>
        )}
      </span>
    }
  />
);

/**
 * Prospect FitFinder session — deep link `/fitfinder/session/:sessionId` (Stripe return URL).
 * Requires JWT for GET/transition (see implementation delta §3.2).
 */
export const FitfinderSessionPage: React.FC = () => {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [session, setSession] = useState<FitfinderSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkout = searchParams.get('checkout');

  const load = useCallback(async () => {
    if (!sessionId) return;
    if (!SESSION_ID_RE.test(sessionId)) {
      setSession(null);
      setError('Invalid session link. Use the URL from your FitFinder session or Stripe return link.');
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const row = await getFitfinderSession(sessionId);
      setSession(row);
    } catch (e: unknown) {
      setSession(null);
      setError(getApiErrorMessage(e, 'Could not load FitFinder session.'));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (checkout === 'success' || checkout === 'cancelled') {
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      setSearchParams(next, { replace: true });
      if (checkout === 'success') {
        void load();
      }
    }
  }, [checkout, load, searchParams, setSearchParams]);

  const onContinueToQualified = async () => {
    if (!sessionId) return;
    setActionLoading(true);
    setError(null);
    try {
      const out = await transitionFitfinderSession(sessionId, 'IN_PROGRESS', {
        return_origin: window.location.origin,
      });
      if (out.payment_required && out.checkout_url) {
        window.location.assign(out.checkout_url);
        return;
      }
      setSession(out);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not start qualified session checkout.'));
    } finally {
      setActionLoading(false);
    }
  };

  if (!sessionId) {
    return (
      <div style={{ maxWidth: 640, margin: '40px auto', padding: 24 }}>
        <Alert type="error" message="Missing session id" showIcon />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: '40px auto', padding: 24 }}>
      <Card>
        <Title level={3} style={{ marginTop: 0 }}>
          FitFinder session
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Session <Text code>{sessionId}</Text>
        </Paragraph>
        {loading && <Paragraph>Loading…</Paragraph>}
        {error && (
          <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />
        )}
        {!loading && session && (
          <>
            <Space wrap style={{ marginBottom: 16 }}>
              <Text strong>State:</Text>
              <Tag color="blue">{session.state}</Tag>
              {session.in_progress_paid_at && (
                <Tag color="green">Paid {session.in_progress_paid_at}</Tag>
              )}
            </Space>
            <Paragraph>
              Solution item: <Text code>{session.solution_artifact_item_id}</Text>
            </Paragraph>
            {session.state === 'INITIALIZED' && session.in_progress_paid_at && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Payment recorded"
                description="Complete the transition to start your qualified session (no second checkout if the webhook already applied your payment)."
              />
            )}
            {session.state === 'INITIALIZED' && (
              <Paragraph>
                {session.in_progress_paid_at
                  ? 'Click below to move this session to In progress.'
                  : 'Continue to a qualified session ($50) when you are ready. You will be redirected to Stripe Checkout if payment is required.'}
              </Paragraph>
            )}
            {session.state === 'INITIALIZED' && (
              <Button type="primary" loading={actionLoading} onClick={() => void onContinueToQualified()}>
                {session.in_progress_paid_at ? 'Complete session start' : 'Continue to qualified session ($50)'}
              </Button>
            )}
            {session.state === 'IN_PROGRESS' && (
              <Alert
                type="success"
                showIcon
                style={{ marginTop: 16 }}
                message="Qualified session in progress"
                description="Your payment has been recorded or the session is active. Follow your coach or Krybot flow from here."
              />
            )}
            {session.deal_match && <SessionDealMatchAlert deal={session.deal_match} />}
          </>
        )}
        <div style={{ marginTop: 24 }}>
          <Button type="link" onClick={() => navigate(-1)} style={{ paddingLeft: 0 }}>
            Back
          </Button>
        </div>
      </Card>
    </div>
  );
};
